import { Film, Map, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Chapter, Reveal } from "./Chapter";
import { IMAGES, YOUTUBE_URL } from "../lib/data";

const PHOTOS = [
  { src: IMAGES.piscineFamille, alt: "Vacances à la piscine du camping", rotate: "rotate-2" },
  { src: IMAGES.baignade, alt: "Baignade en Aveyron", rotate: "-rotate-2" },
  { src: IMAGES.animations, alt: "Animations pour enfants au camping", rotate: "rotate-1" },
  { src: IMAGES.peche, alt: "Pêche au lac de Bannac", rotate: "-rotate-1" },
];

export default function Galerie() {
  return (
    <section id="galerie" className="relative py-28 lg:py-36 px-6 sm:px-10 lg:px-16 bg-cream grain overflow-hidden" data-testid="section-galerie">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <Chapter num="2" label="En images" />
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight text-ink">
                Le camping en images,<br />
                <span className="italic font-medium text-pine">comme si vous y étiez.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-ink/60 leading-relaxed">
              Deux minutes au bord du lac : laissez-vous porter par l'ambiance du camping,
              entre chant des oiseaux et éclats de rire.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-8 items-start">
          <Reveal className="lg:col-span-7" delay={0.15}>
            <div className="group -rotate-1 transition-transform duration-500 hover:rotate-0">
              <div className="overflow-hidden rounded-[2rem] shadow-2xl shadow-ink/20 border-8 border-sand">
                <div className="relative aspect-video bg-lakedeep">
                  <iframe
                    src={YOUTUBE_URL}
                    title="Vidéo du Flower Camping Le Lac aux Oiseaux"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    data-testid="galerie-video"
                  />
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3 px-2">
                <span className="grid place-items-center w-10 h-10 rounded-full bg-terra text-sand">
                  <Film size={17} />
                </span>
                <span className="font-hand text-2xl text-lake -rotate-1">le camping en mouvement — 2 minutes d'évasion</span>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5 grid grid-cols-2 gap-5">
            {PHOTOS.map((p, i) => (
              <Reveal key={p.alt} delay={0.15 + i * 0.08} className={i % 2 ? "mt-8" : ""}>
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className={`${p.rotate} rounded-3xl border-4 border-sand shadow-xl shadow-ink/15 w-full h-44 sm:h-52 object-cover transition-transform duration-500 hover:rotate-0 hover:scale-[1.03]`}
                  data-testid={`galerie-photo-${i}`}
                />
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.3}>
          <Link
            to="/plan-du-camping"
            data-testid="galerie-plan-link"
            className="group mt-10 flex items-center justify-between gap-4 rounded-[1.75rem] bg-pine text-sand p-6 sm:p-7 transition-all duration-300 hover:bg-pinedark hover:-translate-y-1 hover:shadow-xl hover:shadow-pine/25"
          >
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-12 h-12 shrink-0 rounded-full bg-gold text-pinedark transition-transform duration-300 group-hover:-rotate-12">
                <Map size={22} />
              </span>
              <div>
                <div className="font-display font-bold text-xl sm:text-2xl">Le plan du camping</div>
                <p className="text-sm text-sand/70 mt-0.5">
                  Tous les emplacements numérotés, la piscine, la mini-ferme… repérez votre coin préféré.
                </p>
              </div>
            </div>
            <ArrowUpRight size={22} className="text-gold shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
